/**
 * useDescription hook.
 */
import Event from '@shared/classes/event';
import EpgContext from '@shared/components/utils/context/epgContext';
import MainContext from '@shared/components/utils/context/mainContext';
import MosaicContext from '@shared/components/utils/context/mosaicContext';
import useMetadata from '@shared/components/utils/hooks/useMetadata';
import { ContextType } from '@shared/types/enums/context';
import { DescriptionContextProps } from '@shared/components/utils/context/descriptionContext';
import { selectMainAsset } from '@shared/store/slices/main.slice';
import { setDescription } from '@shared/store/slices/app.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useContext, useEffect, useMemo } from 'react';
import DateUtils from '@shared/utils/date';
import Focus from '@shared/types/enums/focus';
import { eventsSelector } from '@shared/store/slices/events.slice';
import { focusSelector } from '@shared/store/slices/focus.slice';

export default function useDescription(
  type: ContextType
): DescriptionContextProps {
  // Dispatch
  const dispatch = useAppDispatch();

  // Context
  const epg = useContext(EpgContext);
  const main = useContext(MainContext);
  const mosaic = useContext(MosaicContext);

  // Selectors
  const mainAsset = useAppSelector(selectMainAsset);
  const { focus, stack } = useAppSelector(focusSelector);
  const { currentEvents } = useAppSelector(eventsSelector);

  /**
   * With the menu open the description can only be displayed on timeline
   * pages (channels grid), where it shows the first channel's live event
   * instead of the last focused one.
   */
  const menuAsset = useMemo(() => {
    if (type !== ContextType.MAIN) return undefined;
    if (focus !== Focus.MENU && !stack.includes(Focus.MENU)) return undefined;
    const events = main.rails?.[0]?.data as Event[] | undefined;
    const channelId = events?.[0]?.channel?.id;
    return (
      events?.find((item) =>
        DateUtils.isCurrentDate(item?.date?.start, item?.date?.end)
      ) ?? (channelId ? currentEvents[channelId] : undefined)
    );
  }, [currentEvents, focus, main, stack, type]);

  // Retrieve parameters
  const { asset, display, alternateTip } = useMemo(() => {
    switch (type) {
      case ContextType.EPG:
        return {
          asset: epg.asset,
          display: epg.config.display.description,
        };
      case ContextType.MAIN: {
        const asset = menuAsset ?? mainAsset;
        return {
          asset,
          display: main.config.display.description,
          alternateTip:
            DateUtils.isPastDate(asset?.date?.end) &&
            !asset?.channel?.hasYouMissedIt(),
        };
      }
      case ContextType.MOSAIC:
        return {
          asset: mosaic.asset,
          display: mosaic.config.description,
        };
    }
    return { asset: undefined, display: false };
  }, [epg, main, mainAsset, menuAsset, mosaic, type]);

  // Effects

  useEffect(() => {
    dispatch(setDescription(display));
  }, [display]);

  useEffect(() => {
    return () => {
      dispatch(setDescription(false));
    };
  }, []);

  // Metadata
  const metadata = useMetadata({ asset: asset as Event });
  // Return values
  return {
    asset: asset as Event | null,
    display,
    metadata,
    alternateTip,
  };
}
